import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getVideoBySlug, getRelatedVideos } from '@/mocks/videos';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const video = slug ? getVideoBySlug(slug) : null;

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <header>
          <Navbar />
        </header>
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
              视频未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              您查找的视频不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = getRelatedVideos(video);
  const pageTitle = `${video.title} - 打屁股视频大全`;
  const pageDescription = video.description.substring(0, 160);

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>
      <main className="pt-16">
        <div className="bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: video.categoryName, href: `/category/${video.categorySlug}` },
                { label: video.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${video.categorySlug}`}
                  className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full font-medium hover:bg-primary-200 transition-colors"
                >
                  {video.categoryName}
                </Link>
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 leading-snug">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {video.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {video.views >= 10000
                    ? `${(video.views / 10000).toFixed(1)}万次观看`
                    : `${video.views}次观看`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  时长 {video.duration}
                </span>
              </div>
            </div>

            <div className="aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={video.thumbnailUrl}
                alt={video.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              <div className="bg-background-100/70 rounded-lg p-6 mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  视频简介
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm whitespace-pre-line">
                  {video.description}
                </p>
              </div>

              <div className="bg-background-100/70 rounded-lg p-6 mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  视频标签
                </h2>
                <div className="flex flex-wrap gap-2">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-sm bg-secondary-100 text-secondary-700 px-4 py-1.5 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {relatedVideos.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedVideos.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group flex gap-4 bg-background-100 rounded-lg p-3 cursor-pointer hover:bg-background-200/70 transition-colors"
                    >
                      <div className="w-40 flex-shrink-0 aspect-video rounded-md overflow-hidden">
                        <img
                          src={rel.thumbnailUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {rel.views >= 10000
                              ? `${(rel.views / 10000).toFixed(1)}万`
                              : rel.views}
                          </span>
                          <span>{rel.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: video.title,
            description: pageDescription,
            datePublished: video.date,
            url: `${siteInfo.url}/article/${video.slug}`,
            inLanguage: 'zh-CN',
            author: {
              '@type': 'Organization',
              name: siteInfo.name,
            },
            publisher: {
              '@type': 'Organization',
              name: siteInfo.name,
              url: siteInfo.url,
            },
            image: video.thumbnailUrl,
            about: video.tags.map((tag) => ({ '@type': 'Thing', name: tag })),
          }),
        }}
      />
    </>
  );
}